import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { stakeholderFormSchema, type StakeholderFormValues } from './stakeholderFormSchema'
import type { Stakeholder, StakeholderUpsert } from '../types'

interface Props {
  visible: boolean
  initial: Stakeholder | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: StakeholderUpsert) => void
  onHide: () => void
}

/** Paydaş/katılımcı ekle/düzenle formu (022: react-hook-form + zod). TR ad zorunlu; EN opsiyonel. Sıra formda yok. */
export default function StakeholderFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(stakeholderFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: StakeholderFormValues) =>
    onSubmit({ nameTr: v.nameTr, nameEn: v.nameEn ? v.nameEn : null })

  return (
    <FormDialog
      visible={visible}
      header={
        initial
          ? t('activity.stakeholders.form.editTitle')
          : t('activity.stakeholders.form.newTitle')
      }
      submitLabel={t('activity.stakeholders.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField
          control={control}
          name="nameTr"
          label={t('activity.stakeholders.form.nameTr')}
          required
          autoFocus
        />
        <FormTextField
          control={control}
          name="nameEn"
          label={t('activity.stakeholders.form.nameEn')}
        />
      </div>
    </FormDialog>
  )
}
